import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../../components/footer/footer";
import Header from "../../components/header/header";
import { ItemNotFound } from "../404/404";
import SearchableSelect from "../../components/design/selectors/SearchableSelect";
import { placeHomeRouterOrder } from "../../services/homeRouters.service";
import { HOME_ROUTERS_PLANS } from "../../../config";
import { HomeRouterPlan } from "../../../types";
import useRequireToken from "./useRequireToken";
import Rs from "../../components/design/rs";
import { routerToItem, trackGenerateLead } from "../../tools/tracking";
import "./homeRouters.css";

// Saudi cities the customer can choose from for delivery
const SAUDI_CITIES: { en: string; ar: string }[] = [
  { en: "Riyadh", ar: "الرياض" },
  { en: "Jeddah", ar: "جدة" },
  { en: "Mecca", ar: "مكة المكرمة" },
  { en: "Medina", ar: "المدينة المنورة" },
  { en: "Dammam", ar: "الدمام" },
  { en: "Al Khobar", ar: "الخبر" },
  { en: "Dhahran", ar: "الظهران" },
  { en: "Taif", ar: "الطائف" },
  { en: "Tabuk", ar: "تبوك" },
  { en: "Buraidah", ar: "بريدة" },
  { en: "Unaizah", ar: "عنيزة" },
  { en: "Khamis Mushait", ar: "خميس مشيط" },
  { en: "Abha", ar: "أبها" },
  { en: "Hail", ar: "حائل" },
  { en: "Najran", ar: "نجران" },
  { en: "Jubail", ar: "الجبيل" },
  { en: "Yanbu", ar: "ينبع" },
  { en: "Al Hofuf", ar: "الهفوف" },
  { en: "Al Qatif", ar: "القطيف" },
  { en: "Hafr Al-Batin", ar: "حفر الباطن" },
  { en: "Jizan", ar: "جازان" },
  { en: "Arar", ar: "عرعر" },
  { en: "Sakaka", ar: "سكاكا" },
  { en: "Al Bahah", ar: "الباحة" },
];

// pick the EN/AR field of a plan based on the active language
const localized = (plan: HomeRouterPlan, key: string, lang: string) =>
  (plan as any)[key + (lang === "ar" ? "Ar" : "En")];

type Customer = {
  nationalId: string;
  fullName: string;
  email: string;
  contactNumber: string;
  alternativeContactNumber: string;
  city: string;
};

const EMPTY: Customer = {
  nationalId: "",
  fullName: "",
  email: "",
  contactNumber: "",
  alternativeContactNumber: "",
  city: "",
};

// ID type is derived silently from the national ID and sent to the backend:
// IDs starting with "1" are National IDs ("1"), IDs starting with "2" are
// Iqamas ("2") — validation rejects anything else. The API requires the value
// as a string ("1" or "2").
const deriveIdType = (nationalId: string): string =>
  nationalId.startsWith("1") ? "1" : "2";

// exactly 10 digits, starting with 1 (National ID) or 2 (Iqama)
const NATIONAL_ID_RE = /^[12]\d{9}$/;

// letters (Latin or Arabic) and spaces only — no digits or special characters
const NAME_RE = /^[A-Za-zء-ي][A-Za-zء-ي\s]*$/;
const stripNonNameChars = (v: string) =>
  v.replace(/[^A-Za-zء-ي\s]/g, "");

const HomeRouterSubmit: React.FC = () => {
  useRequireToken();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const allPlans = HOME_ROUTERS_PLANS as HomeRouterPlan[];
  const index = Number(id);
  const plan =
    !Number.isNaN(index) && index >= 0 && index < allPlans.length
      ? allPlans[index]
      : undefined;

  const [form, setForm] = React.useState<Customer>(EMPTY);
  const [errors, setErrors] = React.useState<
    Partial<Record<keyof Customer, string>>
  >({});
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // visitors opened from the mobile app (detected via a token in the deep-link
  // URL) get a "close" action that hands control back to the native host
  // instead of navigating to the website
  const fromApp = localStorage.getItem("fromApp") === "true";

  // best-effort: tell the native host to close the web view. The exact bridge
  // depends on the app; we try the common ones and fall back to window.close().
  const closeWebView = () => {
    const w = window as any;
    try {
      if (w.ReactNativeWebView?.postMessage) {
        w.ReactNativeWebView.postMessage(JSON.stringify({ type: "CLOSE_WEBVIEW" }));
        return;
      }
      if (w.webkit?.messageHandlers?.close?.postMessage) {
        w.webkit.messageHandlers.close.postMessage("close");
        return;
      }
      if (typeof w.Android?.closeWebView === "function") {
        w.Android.closeWebView();
        return;
      }
    } catch (e) {
      /* ignore and fall through */
    }
    window.close();
  };

  if (!plan) {
    return (
      <>
        <div className="home-routers__section">
          <div className="container">
            <Header />
            <ItemNotFound
              title={t("home_routers.no_plans_found")}
              description={t("home_routers.no_plans_description")}
              button={t("home_routers.back_to_routers")}
              onClick={() => navigate("/home-routers")}
            />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const planName = localized(plan, "name", lang);
  const totalAmount =
    plan.show === false ? plan.regularPrice : plan.discountedPrice;

  const set = (key: keyof Customer, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof Customer, string>> = {};
    if (!NATIONAL_ID_RE.test(form.nationalId))
      e.nationalId = t("home_routers.err_national_id") as string;
    if (!form.fullName.trim())
      e.fullName = t("home_routers.err_required") as string;
    else if (!NAME_RE.test(form.fullName.trim()))
      e.fullName = t("home_routers.err_name") as string;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = t("home_routers.err_email") as string;
    if (!/^5\d{8}$/.test(form.contactNumber))
      e.contactNumber = t("home_routers.err_phone") as string;
    if (!/^5\d{8}$/.test(form.alternativeContactNumber))
      e.alternativeContactNumber = t("home_routers.err_phone") as string;
    if (!form.city.trim()) e.city = t("home_routers.err_required") as string;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    const payload = {
      planName,
      providerId: plan.providerId,
      totalAmount,
      customer: {
        idType: deriveIdType(form.nationalId),
        nationalId: form.nationalId,
        fullName: form.fullName,
        email: form.email,
        contactNumber: form.contactNumber,
        alternativeContactNumber: form.alternativeContactNumber,
        city: form.city,
      },
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await placeHomeRouterOrder(payload, lang);
      // the API returns HTTP 200 even on failure; the real status is in the body
      if (res && res.isSucceeded === false) {
        setSubmitError(
          res.errorMessage || (t("home_routers.submit_error") as string)
        );
        return;
      }
      // A lead, not a purchase: this flow takes no payment, so sending it as a
      // purchase would book revenue that was never collected and pull ad budget
      // toward it.
      trackGenerateLead({
        items: [routerToItem(plan, index, lang)],
        value: totalAmount,
        leadType: "home_router_subscription",
        orderId: res?.returnData?.tygoOrderId ?? null,
      });
      setDone(true);
      // the form leaves the page scrolled down; show the success screen from the top
      window.scrollTo(0, 0);
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          (t("home_routers.submit_error") as string)
      );
    } finally {
      setSubmitting(false);
    }
  };

  // localSim-style text/number input wrapped in `.input_bg`
  const field = (
    key: keyof Customer,
    label: string,
    opts: {
      type?: string;
      placeholder?: string;
      maxLength?: number;
      optional?: boolean;
      inputMode?: "text" | "numeric" | "tel" | "email";
      sanitize?: (value: string) => string;
    } = {}
  ) => (
    <div className="form-group">
      <label>
        {label}
        {!opts.optional ? <span style={{ color: "#e2574c" }}> *</span> : null}
      </label>
      <div
        className="input_bg d-flex gap-3 align-items-center mt-2"
        style={{ padding: "10px" }}
      >
        <input
          className={`form-control form-control-lg${
            errors[key] ? " is-invalid" : ""
          }`}
          type={opts.type || "text"}
          inputMode={opts.inputMode}
          maxLength={opts.maxLength}
          placeholder={opts.placeholder}
          value={form[key]}
          onChange={(e) =>
            set(
              key,
              opts.sanitize ? opts.sanitize(e.target.value) : e.target.value
            )
          }
        />
      </div>
      {errors[key] ? <p className="text-danger mt-1">{errors[key]}</p> : null}
    </div>
  );

  // mobile number input — same look as the local-SIM steps (Saudi flag + +966)
  const phoneField = (
    key: keyof Customer,
    label: string,
    opts: { optional?: boolean } = {}
  ) => (
    <div className="form-group">
      <label>
        {label}
        {!opts.optional ? <span style={{ color: "#e2574c" }}> *</span> : null}
      </label>
      <div className="d-flex gap-3 align-items-center input_bg mt-2" dir="rtl">
        <input
          className={`form-control form-control-lg${
            errors[key] ? " is-invalid" : ""
          }`}
          style={{ fontSize: "1.25rem", border: 0 }}
          type="tel"
          inputMode="tel"
          maxLength={9}
          placeholder="5XXXXXXXX"
          value={form[key]}
          onChange={(e) => set(key, e.target.value)}
        />
        <div className="country-code d-flex align-items-center px-3">
          <div className="country-flag">
            <img src="https://flagcdn.com/sa.svg" alt="flag" />
          </div>
          <div className="country-code-text">+966</div>
        </div>
      </div>
      {errors[key] ? <p className="text-danger mt-1">{errors[key]}</p> : null}
    </div>
  );

  return (
    <>
      <div className="home-routers__section">
        <div className="container">
          <Header />

          <Link to={`/home-routers/${index}`} className="hr-detail__back">
            <i className={`fas fa-arrow-${lang === "ar" ? "right" : "left"}`} />
            <span>{t("home_routers.back_to_plan")}</span>
          </Link>

          {done ? (
            <div className="hr-submit__success">
              <div className="hr-submit__success-icon">
                <i className="fas fa-check" />
              </div>
              <h2>{t("home_routers.success_title")}</h2>
              <p>{t("home_routers.success_message", { plan: planName })}</p>
              <div className="hr-submit__success-actions">
                <button
                  className="router-card__cta"
                  onClick={() => navigate("/home-routers")}
                >
                  {t("home_routers.new_request")}
                </button>
                {fromApp ? (
                  <button
                    className="hr-submit__success-secondary"
                    onClick={closeWebView}
                  >
                    {t("home_routers.close")}
                  </button>
                ) : (
                  <button
                    className="hr-submit__success-secondary"
                    onClick={() => navigate("/")}
                  >
                    {t("home_routers.home_page")}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="row g-4 pb-5">
              {/* form */}
              <div className="col-12 col-lg-7">
                <form
                  className="hr-submit__form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <h3 className="hr-submit__section-title">
                    {t("home_routers.personal_info")}
                  </h3>

                  {field("nationalId", t("home_routers.national_id"), {
                    inputMode: "numeric",
                    maxLength: 10,
                    placeholder: "1234567890",
                    sanitize: (v) => v.replace(/\D/g, ""),
                  })}
                  {field("fullName", t("home_routers.full_name"), {
                    placeholder: "محمد أحمد",
                    sanitize: stripNonNameChars,
                  })}
                  {field("email", t("home_routers.email"), {
                    type: "email",
                    inputMode: "email",
                    placeholder: "john.doe@example.com",
                  })}
                  {phoneField("contactNumber", t("home_routers.contact_number"))}
                  {phoneField(
                    "alternativeContactNumber",
                    t("home_routers.alt_contact_number")
                  )}

                  {/* city picker (searchable Saudi cities) */}
                  <div className="form-group">
                    <label>
                      {t("home_routers.city")}
                      <span style={{ color: "#e2574c" }}> *</span>
                    </label>
                    <div className="mt-2">
                      <SearchableSelect
                        options={SAUDI_CITIES.map((c) => ({
                          value: c.en,
                          label: lang === "ar" ? c.ar : c.en,
                        }))}
                        value={form.city}
                        onChange={(v) => set("city", v)}
                        placeholder={t("home_routers.select_city") as string}
                        searchPlaceholder={t("home_routers.search_city") as string}
                        noResultsText={t("home_routers.no_cities_found") as string}
                        invalid={!!errors.city}
                        dir={lang === "ar" ? "rtl" : "ltr"}
                      />
                    </div>
                    {errors.city ? (
                      <p className="text-danger mt-1">{errors.city}</p>
                    ) : null}
                  </div>

                  {submitError ? (
                    <p className="text-danger mt-3 mb-0">{submitError}</p>
                  ) : null}

                  <button
                    type="submit"
                    className="router-card__cta hr-submit__cta"
                    disabled={submitting}
                  >
                    {submitting
                      ? t("home_routers.submitting")
                      : t("home_routers.submit_subscription")}
                  </button>
                </form>
              </div>

              {/* order summary */}
              <div className="col-12 col-lg-5">
                <div className="hr-submit__summary">
                  <h3 className="hr-submit__section-title">
                    {t("home_routers.order_summary")}
                  </h3>
                  <div className="hr-submit__summary-row">
                    <span>{t("home_routers.plan")}</span>
                    <strong>{planName}</strong>
                  </div>
                  <div className="hr-submit__summary-row">
                    <span>{t("home_routers.data")}</span>
                    <strong>{localized(plan, "data", lang)}</strong>
                  </div>
                  <div className="hr-submit__summary-row">
                    <span>{t("home_routers.commitment_short")}</span>
                    <strong>{plan.commitmentPeriodMonths}</strong>
                  </div>
                  <div className="hr-submit__summary-total">
                    <span>{t("home_routers.total")}</span>
                    <strong>
                      {totalAmount} <Rs size={20} />
                    </strong>
                  </div>
                  {plan.show === false ? null : (
                    <p className="hr-submit__summary-note">
                      {t("home_routers.then_regular", {
                        price: plan.regularPrice,
                      })}{" "}
                      <Rs size={14} color="#6b6880" />
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default HomeRouterSubmit;
